import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, AlertCircle, Loader2, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfirmAssignmentPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const hasCalledRef = useRef(false);  // Prevent double-call in StrictMode
  
  const [status, setStatus] = useState("loading"); // loading, success, already_confirmed, error, invalid
  const [message, setMessage] = useState("");
  const [errorDetail, setErrorDetail] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setMessage("Invalid confirmation link");
      setErrorDetail("No confirmation token was provided in the URL.");
      return;
    }

    // Prevent double invocation in React StrictMode
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;
    
    confirmAssignment();
  }, [token]);

  const confirmAssignment = async () => {
    try {
      const response = await axios.get(`${API}/confirm-assignment/${token}`);
      
      if (response.data.already_confirmed) {
        setStatus("already_confirmed");
        setMessage("Assignment already confirmed");
      } else {
        setStatus("success");
        setMessage("Assignment confirmed successfully!");
      }
    } catch (error) {
      console.error("Confirmation error:", error);
      
      if (error.response?.status === 404) {
        setStatus("invalid");
        setMessage("Invalid confirmation link");
        setErrorDetail("This confirmation link is invalid or has expired. Please contact your supervisor if you believe this is an error.");
      } else if (error.response?.status === 400) {
        setStatus("error");
        setMessage("Could not confirm assignment");
        setErrorDetail(error.response?.data?.detail || "You may no longer be assigned to this job.");
      } else {
        setStatus("error");
        setMessage("Something went wrong");
        setErrorDetail("Please try again later or contact support if the problem persists.");
      }
    }
  };

  const getIcon = () => {
    switch (status) {
      case "loading":
        return <Loader2 className="w-16 h-16 text-[#F37021] animate-spin" />;
      case "success":
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case "already_confirmed":
        return <CheckCircle className="w-16 h-16 text-blue-500" />;
      case "invalid":
        return <XCircle className="w-16 h-16 text-red-500" />;
      case "error":
        return <AlertCircle className="w-16 h-16 text-amber-500" />;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (status) {
      case "success":
        return "bg-green-50 border-green-200";
      case "already_confirmed":
        return "bg-blue-50 border-blue-200";
      case "invalid":
        return "bg-red-50 border-red-200";
      case "error":
        return "bg-amber-50 border-amber-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <Card className={`max-w-md w-full shadow-xl border-2 ${getBackgroundColor()}`}>
        <CardContent className="pt-8 pb-6 px-6 text-center">
          {/* Aggreko Logo/Header */}
          <div className="mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-10 h-10 bg-[#F37021] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <span className="text-2xl font-bold text-gray-800">AGGREKO</span>
            </div>
            <p className="text-sm text-gray-500">Assignment Confirmation</p>
          </div>

          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {getIcon()}
          </div>

          {/* Message */}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {status === "loading" ? "Confirming your assignment..." : message}
          </h1>

          {/* Additional info based on status */}
          {status === "success" && (
            <div className="mt-4 space-y-3">
              <p className="text-green-700">
                Thank you for confirming! Your supervisor has been notified that you have received the assignment details.
              </p>
              <p className="text-sm text-gray-600">
                Please review any attached documents before starting work.
              </p>
            </div>
          )}

          {status === "already_confirmed" && (
            <div className="mt-4 space-y-3">
              <p className="text-blue-700">
                You have already confirmed this assignment. No further action is needed.
              </p>
            </div>
          )}

          {(status === "invalid" || status === "error") && errorDetail && (
            <div className="mt-4">
              <p className="text-gray-600 text-sm">
                {errorDetail}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 space-y-3">
            {status !== "loading" && (
              <>
                <Link to="/login">
                  <Button className="w-full bg-[#F37021] hover:bg-[#e06010] text-white">
                    <Home className="w-4 h-4 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
                
                {(status === "invalid" || status === "error") && (
                  <p className="text-xs text-gray-500 mt-4">
                    Need help? Contact your supervisor or email support.
                  </p>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
